// DOCS → TIMELINE — the chain, in the order it happened.
//
// The other three tabs each show one link of Materials → Features → Tasks →
// Evidence as it stands NOW. This one shows all four AS THEY MOVED, newest
// first, because the chain's argument is a sequence: a material lands, the
// review reworks the features it touched, a task is minted off one of those
// features and filed, evidence lands on the task. Read a tab at a time that
// story is invisible; read down this column and it is the only thing there is.
//
// It is a pure read of the ledgers — GET /api/docs/timeline derives every event
// from what the chain already wrote (see backend/modules/requirements/
// docs-timeline.js). Nothing here can be edited, and there is no write path on
// this surface at all.
//
// TWO THINGS MAKE IT READABLE RATHER THAN A LOG
//   1. VERSION BOUNDARIES are drawn, not just mentioned. A materials mint or a
//      feature review is a rule across the stream with the version on it, so
//      "which version was this under" is answered by looking up the page
//      instead of reading a badge on every row.
//   2. EVERY EVENT LINKS to the object it is about, in the tab that owns it —
//      a material item opens on Materials at that item, a feature opens the
//      Features tab focused on it, a task opens Tasks with its row expanded.
//      A timeline whose entries are dead ends is a changelog, and the product
//      already has one of those.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, RefreshCw, History, FileText, MessagesSquare, Pencil, EyeOff,
  Sparkles, ListChecks, ClipboardList, Check, ShieldCheck, GitCommitHorizontal,
  UploadCloud, AlertTriangle, ChevronRight, Eye, FastForward, Rocket,
} from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";

// The four families, in chain order — the same order as the tabs, so the
// filter row reads as the chain rather than as an alphabetical list.
const KINDS = [
  ["materials", "Sources"],
  ["build", "Build"],
  ["features", "Requirements"],
  ["tasks", "Tasks"],
  ["evidence", "Evidence"],
];

// One icon per event TYPE, not per family: "a file was uploaded" and "a request
// was typed" are both materials events and a reader tells them apart at a
// glance only if they do not share a glyph.
const TYPE_META = {
  "materials.version": { icon: History, tone: "text-nav-active" },
  "material.uploaded": { icon: UploadCloud, tone: "text-page-muted" },
  "material.added": { icon: MessagesSquare, tone: "text-page-muted" },
  "material.edited": { icon: Pencil, tone: "text-amber-700 dark:text-amber-400" },
  "material.removed": { icon: EyeOff, tone: "text-red-700 dark:text-red-400" },
  "features.review": { icon: Sparkles, tone: "text-nav-active" },
  "feature.added": { icon: Sparkles, tone: "text-emerald-700 dark:text-emerald-400" },
  "feature.changed": { icon: Pencil, tone: "text-amber-700 dark:text-amber-400" },
  "feature.status": { icon: Check, tone: "text-nav-active" },
  "feature.missing": { icon: AlertTriangle, tone: "text-red-700 dark:text-red-400" },
  "task.created": { icon: ClipboardList, tone: "text-page-muted" },
  "task.filed": { icon: ListChecks, tone: "text-nav-active" },
  "task.done": { icon: Check, tone: "text-emerald-700 dark:text-emerald-400" },
  "task.failed": { icon: AlertTriangle, tone: "text-red-700 dark:text-red-400" },
  "task.cancelled": { icon: EyeOff, tone: "text-page-muted" },
  "evidence.added": { icon: ShieldCheck, tone: "text-emerald-700 dark:text-emerald-400" },
  // The BUILD stage of a sources version: preview → decision → the one card → analysis.
  "preview.booked": { icon: Sparkles, tone: "text-page-muted" },
  "preview.failed": { icon: AlertTriangle, tone: "text-red-700 dark:text-red-400" },
  "preview.round": { icon: Eye, tone: "text-nav-active" },
  "preview.comment": { icon: MessagesSquare, tone: "text-amber-700 dark:text-amber-400" },
  "preview.approved": { icon: Check, tone: "text-emerald-700 dark:text-emerald-400" },
  "preview.skipped": { icon: FastForward, tone: "text-page-muted" },
  "preview.dismissed": { icon: EyeOff, tone: "text-page-muted" },
  "preview.not_needed": { icon: FastForward, tone: "text-page-muted" },
  "build.filed": { icon: Rocket, tone: "text-nav-active" },
  "build.started": { icon: Loader2, tone: "text-amber-700 dark:text-amber-400" },
  "build.done": { icon: Check, tone: "text-emerald-700 dark:text-emerald-400" },
  "build.failed": { icon: AlertTriangle, tone: "text-red-700 dark:text-red-400" },
  "build.cancelled": { icon: EyeOff, tone: "text-page-muted" },
  "analysis.booked": { icon: Sparkles, tone: "text-nav-active" },
};
const typeMeta = (t) => TYPE_META[t] || { icon: GitCommitHorizontal, tone: "text-page-muted" };

// Day headings. "Today" and "Yesterday" are what a person actually calls the
// days they are most likely to be reading about.
function dayKey(ts) {
  return String(ts || "").slice(0, 10);
}
function dayLabel(key) {
  if (!key) return "Undated";
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const yest = new Date(today.getTime() - 86_400_000);
  if (key === iso(today)) return "Today";
  if (key === iso(yest)) return "Yesterday";
  const d = new Date(`${key}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? key
    : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}
// 24-hour, always. The gutter is a fixed narrow column and a locale that adds
// " PM" wraps every single row onto two lines — the stream stops scanning as a
// column of times the moment that happens.
function timeLabel(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function TimelineTab({ projectSlug, jobs, onOpenMaterial, onOpenFeature, onOpenTask }) {
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);      // events accumulated across "load more"
  const [cursor, setCursor] = useState(null);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  // Which families are shown. Empty = all — the server reads it the same way,
  // so an all-on filter costs one query rather than four.
  const [kinds, setKinds] = useState(() => new Set());

  const query = useMemo(() => {
    const p = [];
    if (kinds.size && kinds.size < KINDS.length) p.push(`kinds=${[...kinds].join(",")}`);
    return p.length ? `?${p.join("&")}` : "";
  }, [kinds]);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await fetchData(`docs/timeline${query}`, { method: "GET" });
      setData(r);
      setPages(r?.events || []);
      setCursor(r?.cursor || null);
      setMore(!!r?.more);
    } catch (e) {
      setData({ events: [], counts: {} });
      setPages([]);
      setErr(String(e?.message || "Could not read the timeline."));
    }
  }, [query]);
  useEffect(() => { setData(null); load(); }, [projectSlug, load]);

  // A review that just finished has written a features version — the stream is
  // stale the moment it lands, so re-read on the same signal the other tabs use.
  const running = (jobs || []).filter((j) => j.kind === "matrix" && j.status === "running");
  const prevRunning = React.useRef(0);
  useEffect(() => {
    if (prevRunning.current > 0 && running.length < prevRunning.current) load();
    prevRunning.current = running.length;
  }, [running.length, load]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const sep = query ? "&" : "?";
      const r = await fetchData(`docs/timeline${query}${sep}before=${encodeURIComponent(cursor)}`, { method: "GET" });
      setPages((p) => [...p, ...(r?.events || [])]);
      setCursor(r?.cursor || null);
      setMore(!!r?.more);
    } catch (e) {
      setErr(String(e?.message || "Could not load more."));
    } finally { setLoadingMore(false); }
  };

  const toggleKind = (k) => setKinds((s) => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  // Group into days, preserving the server's order inside each day.
  const days = useMemo(() => {
    const out = [];
    let cur = null;
    for (const e of pages) {
      const k = e.ts ? dayKey(e.ts) : "";
      if (!cur || cur.key !== k) { cur = { key: k, events: [] }; out.push(cur); }
      cur.events.push(e);
    }
    return out;
  }, [pages]);

  const counts = data?.counts || {};
  const total = data?.total || 0;

  const openEvent = (e) => {
    const t = e.target || {};
    if (t.tab === "materials" && t.id) return onOpenMaterial?.(t.id);
    if (t.tab === "features") return onOpenFeature?.(t.key || null);
    if (t.tab === "tasks") return onOpenTask?.(t.task_id || null);
    if (t.tab === "materials") return onOpenMaterial?.(null);
    return undefined;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="timeline-tab">
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <History size={20} /> Timeline
        </h1>
        <div className="text-sm text-page-muted mt-1">
          every change to <b>{projectSlug}</b>'s chain in the order it happened — materials
          landing, reviews reworking features, tasks filed, evidence attached.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {KINDS.map(([k, label]) => {
          const on = kinds.size === 0 || kinds.has(k);
          const n = counts[k] ?? 0;
          return (
            <button
              key={k} type="button" data-testid={`timeline-filter-${k}`}
              onClick={() => toggleKind(k)}
              aria-pressed={kinds.has(k)}
              title={kinds.has(k) ? `Stop filtering to ${label}` : `Show only ${label}`}
              className={
                "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border cursor-pointer " +
                (kinds.has(k)
                  ? "border-nav-active/40 bg-nav-active/10 text-nav-active"
                  : "border-page-divider bg-transparent text-page-muted hover:text-page-text")
              }
              style={on || kinds.has(k) ? undefined : { opacity: 0.55 }}
            >
              {label} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
        {kinds.size ? (
          <button type="button" data-testid="timeline-filter-clear" onClick={() => setKinds(new Set())}
            className="text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0">
            show all
          </button>
        ) : null}
        <span className="text-[11px] text-page-muted ml-1" data-testid="timeline-total">
          {pages.length} of {total} event(s)
        </span>
        <button type="button" data-testid="timeline-refresh" onClick={load}
          className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1" title="Reload">
          <RefreshCw size={13} />
        </button>
      </div>

      {err ? <div className="text-sm text-red-600" data-testid="timeline-error">{err}</div> : null}
      {data == null && (
        <div className="flex items-center gap-2 text-sm text-page-muted"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      )}
      {data != null && !pages.length && !err && (
        <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2" data-testid="timeline-empty">
          <div className="font-medium">Nothing has happened here yet.</div>
          <div className="text-page-muted">
            {kinds.size
              ? "No events of the kinds you are filtering to. Clear the filter to see the rest."
              : "Add a material or build the feature list, and every step of the chain shows up here in order."}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {days.map((d) => (
          <div key={d.key || "undated"} data-testid={`timeline-day-${d.key || "undated"}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{dayLabel(d.key)}</span>
              <span className="flex-1 h-px" style={{ background: "var(--card-border, rgba(127,127,127,.18))" }} />
            </div>
            {/* The rail the events hang off — one continuous line per day, so
                the column reads as a single stream rather than as cards. */}
            <div className="relative pl-5">
              <span className="absolute left-[7px] top-1 bottom-1 w-px"
                style={{ background: "var(--card-border, rgba(127,127,127,.22))" }} aria-hidden="true" />
              <div className="space-y-0.5">
                {d.events.map((e, i) => <Event key={`${e.ts}-${e.type}-${i}`} e={e} onOpen={openEvent} />)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {more ? (
        <button type="button" data-testid="timeline-more" onClick={loadMore} disabled={loadingMore}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-page-divider bg-transparent text-page-text cursor-pointer hover:bg-nav-active/5 disabled:opacity-50">
          {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null} Load older events
        </button>
      ) : null}
    </div>
  );
}

// One event. A VERSION BOUNDARY renders as a rule with the version on it — a
// visual separator, because that is what a minted version is: everything below
// it happened under the previous one.
function Event({ e, onOpen }) {
  const { icon: Icon, tone } = typeMeta(e.type);
  const clickable = !!e.target && (e.target.id || e.target.key || e.target.task_id || e.target.tab);

  if (e.boundary) {
    return (
      <div className="relative py-1.5" data-testid={`timeline-boundary-${e.type}-${e.features_version ?? e.materials_version ?? ""}`}>
        <span className={`absolute -left-5 top-2 ${tone}`} aria-hidden="true">
          <Icon size={13} />
        </span>
        <button
          type="button"
          onClick={() => clickable && onOpen(e)}
          data-testid={`timeline-event-${e.type}`}
          className="w-full text-left flex items-baseline gap-2 rounded-md px-2 py-1 border-0 cursor-pointer bg-nav-active/5 hover:bg-nav-active/10"
        >
          <span className="text-[11px] text-page-muted tabular-nums shrink-0 w-10">{timeLabel(e.ts)}</span>
          <span className="text-sm font-medium text-page-text min-w-0 flex-1">{e.summary}</span>
          {e.actor ? <Actor who={e.actor} /> : null}
        </button>
        {e.detail ? (
          <div className="pl-14 text-[11px] text-page-muted">{e.detail}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative group" data-testid={`timeline-event-${e.type}`}>
      <span className={`absolute -left-5 top-[7px] ${tone}`} aria-hidden="true">
        <Icon size={12} />
      </span>
      <button
        type="button"
        onClick={() => clickable && onOpen(e)}
        disabled={!clickable}
        title={clickable ? `Open ${e.target.label || "it"}` : undefined}
        className="w-full text-left flex items-baseline gap-2 rounded px-2 py-0.5 border-0 bg-transparent hover:bg-nav-active/5 disabled:cursor-default cursor-pointer"
      >
        <span className="text-[11px] text-page-muted tabular-nums shrink-0 w-10">{timeLabel(e.ts)}</span>
        <span className="text-[13px] text-page-text min-w-0 flex-1">
          {e.summary}
          {e.detail ? <span className="text-page-muted"> — {e.detail}</span> : null}
        </span>
        <VersionBadges e={e} />
        {e.actor ? <Actor who={e.actor} /> : null}
        {clickable ? (
          <ChevronRight size={11} className="shrink-0 text-page-muted opacity-0 group-hover:opacity-100" />
        ) : null}
      </button>
    </div>
  );
}

// The version context an event happened under. Shown on the events, not on the
// boundaries — a boundary already names its own version in its summary, and
// repeating it there is noise.
function VersionBadges({ e }) {
  const badges = [];
  if ((e.kind === "materials" || e.kind === "build") && e.materials_version != null) badges.push(`sources v${e.materials_version}`);
  if (e.kind === "features" && e.features_version != null) badges.push(`features v${e.features_version}`);
  if (!badges.length) return null;
  return (
    <>
      {badges.map((b) => (
        <span key={b} data-testid={`timeline-version-${b.replace(/\s+/g, "-")}`}
          className="shrink-0 text-[10px] px-1.5 py-0.5 rounded text-page-muted"
          style={{ background: "var(--card-border, rgba(127,127,127,.18))" }}>
          {b}
        </span>
      ))}
    </>
  );
}

// Who did it. An email is too long for a stream, and the local part is what a
// colleague is called anyway.
function Actor({ who }) {
  const short = String(who).split("@")[0];
  return (
    <span className="shrink-0 text-[10px] text-page-muted" title={who}>{short}</span>
  );
}
